import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import { toast } from 'react-toastify';

const TITLES: Record<string, string> = {
  designations: 'Designations',
  'sales-stages': 'Sales Stages',
  'opportunity-case-types': 'Opportunity Case Types',
};

interface MasterRow {
  id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
}

const MasterListPage: React.FC = () => {
  const { type = 'designations' } = useParams();
  const [rows, setRows] = useState<MasterRow[]>([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<MasterRow | null>(null);
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');

  const load = () => {
    api.get(`/admin/masters/${type}`).then((res: { data: { data: MasterRow[] } }) => setRows(res.data.data));
  };

  useEffect(() => {
    load();
  }, [type]);

  const openCreate = () => {
    setSelected(null);
    setName('');
    setSortOrder('0');
    setOpen(true);
  };

  const openEdit = (row: MasterRow) => {
    setSelected(row);
    setName(row.name);
    setSortOrder(String(row.sort_order ?? 0));
    setOpen(true);
  };

  const save = async () => {
    const payload = { name, sort_order: Number(sortOrder) || 0, is_active: true };
    try {
      if (selected) {
        await api.put(`/admin/masters/${type}/${selected.id}`, payload);
      } else {
        await api.post(`/admin/masters/${type}`, payload);
      }
      setOpen(false);
      load();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      const first = errors ? Object.values(errors)[0] : null;
      toast.error(Array.isArray(first) ? first[0] : err.response?.data?.message || 'Save failed');
    }
  };

  const deactivate = async (row: MasterRow) => {
    if (!window.confirm(`Deactivate "${row.name}"? Existing records keep it, and it drops out of new dropdowns.`)) {
      return;
    }
    await api.delete(`/admin/masters/${type}/${row.id}`);
    load();
  };

  const reactivate = async (row: MasterRow) => {
    await api.put(`/admin/masters/${type}/${row.id}`, { is_active: true });
    load();
  };

  return (
    <div>
      <Typography variant='h4' gutterBottom>
        {TITLES[type] || 'Master'}
      </Typography>
      <Button variant='contained' onClick={openCreate} sx={{ mb: 2 }}>
        Add
      </Button>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.sort_order}</TableCell>
                <TableCell>{row.is_active ? 'Active' : 'Inactive'}</TableCell>
                <TableCell>
                  <Button size='small' onClick={() => openEdit(row)}>Edit</Button>
                  {row.is_active ? (
                    <Button size='small' color='warning' onClick={() => deactivate(row)}>Deactivate</Button>
                  ) : (
                    <Button size='small' onClick={() => reactivate(row)}>Activate</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>{selected ? 'Edit' : 'Add'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin='normal' label='Name' value={name} onChange={e => setName(e.target.value)} required />
          <TextField fullWidth margin='normal' label='Sort order' type='number' value={sortOrder} onChange={e => setSortOrder(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={save} disabled={!name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MasterListPage;
